import {
  Color,
  PickEventDetail,
  PickingTool,
  Vector3,
  Web3DEventName,
  Web3DViewer,
} from '@technology/web3d';
import { PotreeManagerModel } from '@technology/web3d-plugin-potreemanager';
import {
  CompoundCurve,
  LineEditingModel,
  LineEditingPlugin,
  LineSegment,
  LineType,
} from '@technology/web3d-plugin-line-editing';
import { detectArrayChanges, Web3DViewerWrapper } from '@trimble/utilities';
import {
  MeasurementsCapabilityProvider,
  SnapType,
} from '@trimble/web3d-providers';
import {
  Accessor,
  Setter,
  createEffect,
  createRoot,
  createSignal,
  on,
} from 'solid-js';
import { CapabilityCategory } from '../base/models/capability-category';
import { SharedCapabilityBase } from '../base/shared-capability-base';
import {
  DEFAULT_LINE_DETECTION_WALK_SETTINGS,
  detectLineAlongEdge,
  LineDetectionWalkSettings,
} from './detect-line-along-edge';

const LINE_DETECTION_MODEL_ID = 'line-detection-model';

interface SavedPickingToolState {
  readonly navigation: boolean;
  readonly hover: boolean;
  readonly enabled: boolean;
  readonly activeTool: string;
}

export class LineDetectionCapability extends SharedCapabilityBase {
  public override category: CapabilityCategory = CapabilityCategory.Markup;

  public readonly settings: Accessor<LineDetectionWalkSettings>;
  private readonly setSettings: Setter<LineDetectionWalkSettings>;

  private readonly savedSnapTypes = new WeakMap<Web3DViewer, number[]>();
  private readonly savedPickingToolState = new WeakMap<
    Web3DViewer,
    SavedPickingToolState
  >();
  private readonly lineEditingModels = new WeakMap<
    Web3DViewer,
    LineEditingModel
  >();
  private effectDispose?: () => void;

  constructor(
    measurementsProvider: MeasurementsCapabilityProvider,
    settings: LineDetectionWalkSettings = DEFAULT_LINE_DETECTION_WALK_SETTINGS
  ) {
    super(measurementsProvider);
    const [walkSettings, setWalkSettings] =
      createSignal<LineDetectionWalkSettings>({ ...settings });
    this.settings = walkSettings;
    this.setSettings = setWalkSettings;
  }

  public updateSettings(partial: Partial<LineDetectionWalkSettings>): void {
    this.setSettings({ ...this.settings(), ...partial });
  }

  public resetSettings(): void {
    this.setSettings({ ...DEFAULT_LINE_DETECTION_WALK_SETTINGS });
  }

  public override trackIsEnabled(): void {
    this.enable();
  }

  public start(): Promise<void> {
    this.effectDispose?.();
    this.activate();
    this.setupPicking(this.viewerWrappers());

    this.effectDispose = createRoot((dispose) => {
      createEffect(
        on(this.viewerWrappers, (viewerWrappers, prevViewerWrappers) => {
          if (prevViewerWrappers === undefined) {
            return;
          }
          const { newItems, removedItems } = detectArrayChanges(
            prevViewerWrappers,
            viewerWrappers
          );
          for (const wrapper of removedItems) {
            this.teardownForViewer(wrapper.viewer);
          }
          if (!newItems.length || !this.isActive()) {
            return;
          }
          this.setupPicking(newItems);
        })
      );
      return dispose;
    });
    return Promise.resolve();
  }

  public stop(): Promise<void> {
    this.deactivate();
    return Promise.resolve();
  }

  public override deactivate(): boolean {
    this.effectDispose?.();
    this.effectDispose = undefined;
    for (const viewer of [...this.viewerEventListeners.keys()]) {
      this.teardownForViewer(viewer);
    }
    this.clearAllViewerEventListeners();
    return super.deactivate();
  }

  public override cleanup(): Promise<void> {
    void this.stop();
    return Promise.resolve();
  }

  private setupPicking(viewerWrappers: Web3DViewerWrapper[]): void {
    for (const { viewer } of viewerWrappers) {
      this.removeAllViewerEventListeners(viewer);

      const pickingTool = viewer.tools.get(PickingTool);
      if (!pickingTool) {
        continue;
      }

      if (!this.savedSnapTypes.has(viewer)) {
        this.savedSnapTypes.set(viewer, [...viewer.settings.snapTypes]);
      }
      if (!this.savedPickingToolState.has(viewer)) {
        this.savedPickingToolState.set(viewer, {
          navigation: pickingTool.navigation,
          hover: pickingTool.hover,
          enabled: pickingTool.enabled,
          activeTool: viewer.activeTool ?? '',
        });
      }

      pickingTool.navigation = false;
      pickingTool.hover = false;
      pickingTool.enabled = true;
      viewer.activeTool = pickingTool.name;
      viewer.settings.snapTypes = [SnapType.LINE as number];

      this.addViewerEventListener(
        viewer,
        'pick' as Web3DEventName.Pick,
        (e: Event): void => {
          this.handlePick(e as CustomEvent, viewer);
        }
      );
    }
  }

  private handlePick(e: CustomEvent, viewer: Web3DViewer): void {
    if (!this.isActive()) {
      return;
    }

    const detail = e.detail as PickEventDetail;
    if (detail.hover || detail.multiClick != null) {
      return;
    }

    const worldPosition = detail.worldPosition;
    if (!worldPosition) {
      return;
    }

    const models = viewer.getModels(PotreeManagerModel);
    const linePoints = detectLineAlongEdge(
      models,
      worldPosition,
      this.settings()
    );
    if (!linePoints) {
      return;
    }

    this.addPolyline(viewer, linePoints);
  }

  private addPolyline(viewer: Web3DViewer, points: Vector3[]): void {
    const lineEditing = viewer.plugins.get(LineEditingPlugin);
    if (!lineEditing) {
      return;
    }

    const model = this.getOrCreateLineEditingModel(viewer, lineEditing);

    const segments = points
      .slice(1)
      .map((point, index) => new LineSegment(points[index], point));
    const style = model.createCADStyle(new Color('#ff0000'), 1, LineType.Solid);
    const curve = new CompoundCurve(style, segments, {}, undefined, true, true);

    model.performAction([curve], []);
    model.setSelectionByGuids([curve.guid]);
  }

  private getOrCreateLineEditingModel(
    viewer: Web3DViewer,
    lineEditing: LineEditingPlugin
  ): LineEditingModel {
    const cached = this.lineEditingModels.get(viewer);
    if (cached?.modelId === LINE_DETECTION_MODEL_ID) {
      return cached;
    }

    const model = lineEditing.createModel({
      modelId: LINE_DETECTION_MODEL_ID,
    });
    this.lineEditingModels.set(viewer, model);
    return model;
  }

  private teardownForViewer(viewer: Web3DViewer): void {
    this.removeAllViewerEventListeners(viewer);

    const pickingTool = viewer.tools.get(PickingTool);
    if (pickingTool) {
      pickingTool.cancel();
    }

    const savedSnap = this.savedSnapTypes.get(viewer);
    if (savedSnap !== undefined) {
      viewer.settings.snapTypes = savedSnap;
      this.savedSnapTypes.delete(viewer);
    }

    const savedPicking = this.savedPickingToolState.get(viewer);
    if (savedPicking && pickingTool) {
      pickingTool.navigation = savedPicking.navigation;
      pickingTool.hover = savedPicking.hover;
      pickingTool.enabled = savedPicking.enabled;
      viewer.activeTool = savedPicking.activeTool;
      this.savedPickingToolState.delete(viewer);
    }
  }
}
